import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Coins, Receipt, Plus, Download, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import HeartLoader from "@/components/HeartLoader";

export default function CreditsHistory() {
  const { user } = useApp();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [dl, setDl] = useState("");

  useEffect(() => {
    if (user === null) { nav("/auth"); return; }
    if (!user) return;
    api.get("/credits/history").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [user, nav]);

  const downloadReceipt = async (orderId) => {
    setDl(orderId);
    try {
      const res = await api.get(`/credits/receipt/${orderId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `MyKind-receipt-${orderId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't download the receipt. Please try again.");
    } finally {
      setDl("");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 pt-24 pb-16" data-testid="wallet-page">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-sec hover:text-brand"><ArrowLeft size={15} /> Back</Link>

      <div className="flex items-center gap-2 mt-3">
        <Coins className="text-brand" />
        <h1 className="font-serif text-4xl tracking-tight">Credits &amp; Payments</h1>
      </div>

      <div className="rud-card rounded-2xl p-6 mt-5 flex items-center justify-between" data-testid="wallet-balance-card">
        <div>
          <div className="text-xs uppercase tracking-widest text-sec">Current balance</div>
          <div className="text-3xl font-serif mt-1" data-testid="wallet-balance">{Number(user?.credits || 0).toFixed(2)} <span className="text-lg text-sec">credits</span></div>
        </div>
        <button data-testid="wallet-topup" onClick={() => window.dispatchEvent(new CustomEvent("open-recharge", { detail: { message: "Top up your credits" } }))}
          className="btn-primary h-11 px-5 rounded-full flex items-center gap-2 text-sm">
          <Plus size={16} /> Top up
        </button>
      </div>

      <h2 className="text-base md:text-lg font-medium mt-8 mb-3 flex items-center gap-2"><Receipt size={17} className="text-brand" /> Payment history</h2>

      {items === null ? (
        <div className="py-16"><HeartLoader size={40} label="Loading your payments…" /></div>
      ) : items.length === 0 ? (
        <div className="rud-card rounded-2xl p-8 text-center text-sec" data-testid="wallet-empty">
          No payments yet. When you top up your credits, every payment shows here with a receipt.
        </div>
      ) : (
        <div className="rud-card rounded-2xl divide-y divide-[var(--rud-border)] overflow-hidden" data-testid="wallet-history">
          {items.map((it) => (
            <div key={it.order_id} className="flex items-center justify-between px-5 py-4" data-testid="wallet-history-row">
              <div>
                <div className="font-medium">{it.credits} credits added</div>
                <div className="text-xs text-sec mt-0.5">{fmtDate(it.paid_at)} · <span className="capitalize">{it.provider}</span></div>
                {it.payment_id && <div className="text-[11px] text-sec/70 mt-0.5">ID: {it.payment_id}</div>}
              </div>
              <div className="text-right">
                <div className="font-semibold text-brand">₹{Number(it.amount_inr).toFixed(2)}</div>
                <button data-testid="wallet-download-receipt" onClick={() => downloadReceipt(it.order_id)} disabled={dl === it.order_id}
                  className="mt-1 inline-flex items-center gap-1 text-[11px] text-sec hover:text-brand transition-colors disabled:opacity-60">
                  {dl === it.order_id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Receipt
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
